// Service data shared by the site, the /services pages and proxy.ts.
// Kept free of asset and icon imports so proxy.ts can load it cheaply.

export type Service = {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  stats?: { projects: string; clients: string; satisfaction: string };
  // URL slug for services with a dedicated page; the rest are served at their title-based slug
  slug?: string;
  // Page meta description, when it should differ from `description`
  metaDescription?: string;
  // Set for the services listed on the /services overview
  overview?: {
    number: string;
    summary: string;
    linkLabel: string;
    // Replaces `description` as the page intro
    intro?: string;
  };
};

export type OverviewService = Service & { slug: string; overview: NonNullable<Service["overview"]> };

export const services: Service[] = [
  {
    icon: "BarChart3",
    title: "AI Solutions & Analytics",
    subtitle: "Intelligent Automation & Data Intelligence",
    description:
      "Comprehensive AI-driven solutions combining intelligent automation, machine learning, and advanced analytics to transform your business operations with real-time insights and predictive capabilities",
    features: [
      "Machine Learning & AI Models",
      "Intelligent Process Automation",
      "Advanced Analytics Dashboards",
      "Natural Language Processing",
      "Predictive Analytics & Forecasting",
      "Computer Vision Solutions",
      "Chatbots & Virtual Assistants",
      "Real-time Data Visualization",
      "Business Intelligence & Reporting",
      "AI Strategy Consulting",
    ],
    stats: { projects: "80+", clients: "48+", satisfaction: "99%" },
    id: "analytics",
    slug: "ai-solutions",
    metaDescription:
      "AI-powered software solutions from ORAPEX, integrating machine learning, intelligent automation, and advanced analytics into your business systems.",
    overview: {
      number: "01",
      summary:
        "AI-powered software solutions integrating machine learning and advanced analytics for intelligent business systems.",
      linkLabel: "Explore AI Solutions",
    },
  },
  {
    icon: "GraduationCap",
    title: "Professional Training Programs",
    subtitle: "Skills Development & Certification",
    description:
      "Comprehensive training in Oracle technologies and best practices with certified instructors",
    features: [
      "Certified Instructors",
      "Hands-on Projects",
      "Industry Standards",
      "Continuous Support",
      "Online Learning",
      "Custom Curriculum",
      "Assessment Tools",
      "Career Guidance",
    ],
    stats: { projects: "50+", clients: "200+", satisfaction: "96%" },
    id: "training",
  },
  {
    icon: "Database",
    title: "Oracle APEX Development",
    subtitle: "Enterprise Application Platform",
    description:
      "Custom enterprise applications built on Oracle APEX platform with advanced security and scalability",
    features: [
      "Custom Applications",
      "Enterprise Integration",
      "Advanced Security",
      "Performance Optimization",
      "Real-time Data Processing",
      "Custom Reporting",
      "Workflow Automation",
      "Mobile Responsive",
    ],
    stats: { projects: "75+", clients: "30+", satisfaction: "98%" },
    id: "apex",
  },

  {
    icon: "Settings",
    title: "Oracle EBS Extension using APEX",
    subtitle: "Legacy System Modernization",
    description:
      "Seamless integration and extension of Oracle EBS systems with modern interfaces",
    features: [
      "EBS Integration",
      "Modern UI/UX",
      "Real-time Sync",
      "Custom Workflows",
      "Data Migration",
      "Performance Tuning",
      "Security Enhancement",
      "User Training",
    ],
    stats: { projects: "40+", clients: "25+", satisfaction: "97%" },
    id: "ebs",
  },
  {
    icon: "Code",
    title: "Full-Stack Web Development",
    subtitle: "Modern Web Applications",
    description:
      "End-to-end web application development with modern technologies and frameworks",
    features: [
      "React/Angular/Vue",
      "Node.js/Python",
      "Cloud Deployment",
      "API Development",
      "Database Design",
      "UI/UX Design",
      "Testing & QA",
      "DevOps Integration",
    ],
    stats: { projects: "100+", clients: "45+", satisfaction: "99%" },
    id: "web",
    slug: "web-development",
    metaDescription:
      "Full-stack web application development from ORAPEX, engineered with the functionality, architecture, and integrations your business demands.",
    overview: {
      number: "03",
      summary:
        "Web applications engineered with the functionality, architecture, and integrations your business demands.",
      linkLabel: "Explore Web Development",
    },
  },
  {
    icon: "Smartphone",
    title: "Mobile App Development",
    subtitle: "iOS & Android Solutions",
    description:
      "Native and cross-platform mobile applications with cutting-edge features",
    features: [
      "iOS/Android",
      "React Native",
      "Progressive Web Apps",
      "Push Notifications",
      "Offline Support",
      "App Store Deployment",
      "Analytics Integration",
      "Maintenance Support",
    ],
    stats: { projects: "60+", clients: "35+", satisfaction: "95%" },
    id: "mobile",
    slug: "mobile-app-development",
    metaDescription:
      "Native and cross-platform mobile app development from ORAPEX, built for high performance and seamless functionality across iOS and Android.",
    overview: {
      number: "04",
      summary:
        "Native and cross-platform mobile apps built for high performance and seamless functionality across iOS and Android.",
      linkLabel: "Explore Mobile App Development",
    },
  },
  {
    icon: "Layers",
    title: "ERP Solutions",
    subtitle: "ERP Built Around Your Business",
    description:
      "ERP Solutions provide an integrated foundation for managing business operations. We help you configure, customize, and extend the platform around your workflows, business requirements, and existing systems.",
    features: [
      "ERPNext Implementation",
      "ERP Consulting",
      "ERP Customization",
      "ERPNext Development",
      "ERP Integration",
      "Data Migration",
      "User Training",
      "Ongoing Support",
    ],
    id: "erp",
    slug: "erp-solutions",
    metaDescription:
      "ERP implementation, consulting, customization, integration, and support from ORAPEX to optimize business operations and connect core processes.",
    overview: {
      number: "02",
      summary:
        "A comprehensive suite of ERP services to optimize business operations and connect core processes.",
      linkLabel: "Explore ERP Solutions",
      intro:
        "We implement, customize, and integrate ERP systems around the way your business works, connecting finance, sales, inventory, and operations in one platform.",
    },
  },
];

const isOverviewService = (service: Service): service is OverviewService => Boolean(service.slug && service.overview);

// Services shown on the /services overview, in their numbered order
export const overviewServices = services
  .filter(isOverviewService)
  .sort((a, b) => a.overview.number.localeCompare(b.overview.number));

export const servicesOverview = {
  label: "Our Services",
  heading: "Advanced Technology Solutions for Modern Businesses",
  cta: {
    heading: "Ready to Work Together?",
    button: "Get Started",
  },
};
